import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { CreateManualQuoteInput } from "../../lib/investments";
import { Modal } from "../ui/Modal";

const CURRENCIES = ["EUR", "USD", "GBP", "CHF", "JPY"] as const;
type Currency = (typeof CURRENCIES)[number];

interface Props {
  open: boolean;
  defaultCurrency: Currency;
  onClose: () => void;
  onSubmit: (input: CreateManualQuoteInput) => Promise<void>;
}

function parseEuroDe(input: string): number {
  const trimmed = input.replace(/\./g, "").replace(",", ".").trim();
  const n = Number.parseFloat(trimmed);
  if (Number.isNaN(n) || n <= 0) throw new Error("invalid_amount");
  return Math.round(n * 100);
}

export function QuoteFormModal({ open, defaultCurrency, onClose, onSubmit }: Props) {
  const { t } = useTranslation();
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 16));
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState<Currency>(defaultCurrency);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    setError(null);
    let priceCents: number;
    try {
      priceCents = parseEuroDe(price);
    } catch {
      setError(t("investments.errors.invalid_amount"));
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        asOf: new Date(asOf).toISOString(),
        priceCents,
        currency,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errors.unknown"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={t("investments.quote.add_title")}>
      <div className="space-y-3">
        <label className="block">
          <span className="text-sm">{t("investments.quote.as_of")}</span>
          <input
            type="datetime-local"
            className="border rounded px-2 py-1 w-full"
            value={asOf}
            max={new Date().toISOString().slice(0, 16)}
            onChange={(e) => setAsOf(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-sm">{t("investments.quote.price")}</span>
          <input
            className="border rounded px-2 py-1 w-full"
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="0,00"
          />
        </label>
        <label className="block">
          <span className="text-sm">{t("investments.quote.currency")}</span>
          <select
            className="border rounded px-2 py-1 w-full"
            value={currency}
            onChange={(e) => setCurrency(e.target.value as Currency)}
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        {error && <div className="text-red-700 text-sm">{error}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1 border rounded">
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-3 py-1 bg-blue-600 text-white rounded"
          >
            {submitting ? t("common.submitting") : t("common.save")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
